import Entity from "../../core/entity/Entity";
import { rUniform } from "../../core/util/Random";
import { AsteroidSize } from "../asteroids/AsteroidShapes";
import { LevelEntity } from "../LevelEntity";

// Sound parameters for different asteroid sizes
// Asteroids should sound rocky and crunchy, different from enemy explosions
const ASTEROID_SETTINGS = {
  [AsteroidSize.SMALL]: {
    baseFrequency: 300, // Lowered from 800 to 60-300Hz range
    duration: 0.15,
    volume: 0.15,
    noiseGain: 0.5, // More boom, less crackle
    filterFreq: 800, // Lower filter frequency for less high-end
    crackleIntensity: 0.5, // Reduced crackling
  },
  [AsteroidSize.MEDIUM]: {
    baseFrequency: 250, // Lowered from 600
    duration: 0.25,
    volume: 0.2,
    noiseGain: 0.6,
    filterFreq: 700, // Lower filter
    crackleIntensity: 0.4,
  },
  [AsteroidSize.LARGE]: {
    baseFrequency: 200, // Lowered from 400
    duration: 0.4,
    volume: 0.25,
    noiseGain: 0.7,
    filterFreq: 600, // Lower filter
    crackleIntensity: 0.35,
  },
  [AsteroidSize.XL]: {
    baseFrequency: 150, // Lowered from 300
    duration: 0.6,
    volume: 0.3,
    noiseGain: 0.8,
    filterFreq: 500, // Lower filter
    crackleIntensity: 0.3,
  },
  [AsteroidSize.XXL]: {
    baseFrequency: 100, // Lowered from 200
    duration: 0.8,
    volume: 0.35,
    noiseGain: 0.9,
    filterFreq: 400, // Lower filter
    crackleIntensity: 0.25,
  },
};

export default class AsteroidDestroyedSoundGenerator
  extends LevelEntity
  implements Entity
{
  private audioContext: AudioContext;

  constructor() {
    super();
    this.tags = ["asteroidDestroyedSoundGenerator"];
    this.id = "asteroidDestroyedSoundGenerator";

    // We'll get the audio context from the game when added
    this.audioContext = null as any;
  }

  onAdd() {
    if (!this.game?.audio) {
      console.warn("No audio context available in game");
      return;
    }

    this.audioContext = this.game.audio;
  }

  /**
   * Handle asteroid destroyed events
   */
  onAsteroidDestroyed(eventData: { asteroidSize: number; points: number }) {
    if (!this.audioContext || !this.game?.masterGain) {
      return;
    }

    this.synthesizeRockBreakingSound(eventData.asteroidSize);
  }

  private synthesizeRockBreakingSound(asteroidSize: AsteroidSize) {
    const settings =
      ASTEROID_SETTINGS[asteroidSize] || ASTEROID_SETTINGS[AsteroidSize.MEDIUM];
    const now = this.audioContext.currentTime;

    // Create rock-breaking sound components
    this.createCracklingNoise(now, settings);
    this.createRockCrunchTone(now, settings);
    this.createDebrisScatter(now, settings);
  }

  private createCracklingNoise(now: number, settings: any) {
    // Create crackling texture with filtered noise bursts
    const bufferSize = this.audioContext.sampleRate * settings.duration;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate,
    );
    const data = buffer.getChannelData(0);

    // Generate crackling pattern - not smooth noise like explosions
    for (let i = 0; i < bufferSize; i++) {
      const progress = i / bufferSize;
      const envelope = Math.pow(1 - progress, 1.5); // Sharper decay than explosions

      // Create crackling by modulating noise intensity
      const cracklePhase = progress * settings.crackleIntensity * 100;
      const crackleModulation = Math.sin(cracklePhase) * 0.5 + 0.5;
      const noiseLevel = crackleModulation > 0.7 ? 1.0 : 0.3; // Sharp on/off crackling

      data[i] = rUniform(-1, 1) * envelope * noiseLevel;
    }

    // Create buffer source
    const noiseSource = this.audioContext.createBufferSource();
    noiseSource.buffer = buffer;

    // Bandpass filter for less harsh, more boomy texture
    const noiseFilter = this.audioContext.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.setValueAtTime(settings.filterFreq * 0.6, now); // Lower center frequency
    noiseFilter.frequency.exponentialRampToValueAtTime(
      settings.filterFreq * 0.3,
      now + settings.duration,
    );
    noiseFilter.Q.setValueAtTime(1, now); // Lower Q for wider, less sharp filtering

    // Gain for volume control
    const noiseGain = this.audioContext.createGain();
    noiseGain.gain.setValueAtTime(settings.noiseGain, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + settings.duration);

    // Connect and play
    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.game!.masterGain);

    noiseSource.start(now);
    noiseSource.stop(now + settings.duration);

    // Cleanup
    noiseSource.addEventListener("ended", () => {
      noiseSource.disconnect();
      noiseFilter.disconnect();
      noiseGain.disconnect();
    });
  }

  private createRockCrunchTone(now: number, settings: any) {
    // Low-frequency impact tone for the "crunch"
    const oscillator = this.audioContext.createOscillator();
    oscillator.type = "triangle"; // Softer than sawtooth for rock texture

    // Filter for muffled rock impact
    const filter = this.audioContext.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(settings.filterFreq * 0.8, now);
    filter.frequency.exponentialRampToValueAtTime(
      settings.filterFreq * 0.2,
      now + settings.duration,
    );
    filter.Q.setValueAtTime(0.7, now); // Lower Q for more natural sound

    // Gain for impact envelope
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(settings.volume, now + 0.02); // Slightly slower attack than laser
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      now + settings.duration * 0.7,
    ); // Longer sustain

    // Frequency sweep for breaking/cracking effect - keep in 60-300Hz range
    const startFreq = Math.min(settings.baseFrequency * 1.2, 300); // Cap at 300Hz
    const endFreq = Math.max(settings.baseFrequency * 0.6, 60); // Floor at 60Hz

    oscillator.frequency.setValueAtTime(startFreq, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      endFreq,
      now + settings.duration * 0.8,
    );

    // Connect audio graph
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.game!.masterGain);

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + settings.duration);

    // Cleanup
    oscillator.addEventListener("ended", () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    });
  }

  private createDebrisScatter(now: number, settings: any) {
    // Lower-frequency debris scatter sound (less harsh)
    const debrisDelay = settings.duration * 0.1; // Start shortly after main impact
    const debrisDuration = settings.duration * 0.6; // Shorter than main sound

    const oscillator = this.audioContext.createOscillator();
    oscillator.type = "triangle"; // Softer waveform

    // Bandpass filter for debris texture (less harsh than highpass)
    const filter = this.audioContext.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(
      settings.filterFreq * 1.2,
      now + debrisDelay,
    ); // Much lower than before
    filter.frequency.exponentialRampToValueAtTime(
      settings.filterFreq * 0.8,
      now + debrisDelay + debrisDuration,
    );
    filter.Q.setValueAtTime(1, now + debrisDelay); // Lower Q for softer filtering

    // Gain for debris envelope (quieter than main impact)
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0, now + debrisDelay);
    gainNode.gain.linearRampToValueAtTime(
      settings.volume * 0.2, // Even quieter
      now + debrisDelay + 0.01,
    );
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      now + debrisDelay + debrisDuration,
    );

    // More moderate frequency for scattered debris
    const debrisFreq = settings.baseFrequency * 1.8; // Less high than before
    oscillator.frequency.setValueAtTime(debrisFreq, now + debrisDelay);
    oscillator.frequency.exponentialRampToValueAtTime(
      debrisFreq * 1.2, // Less sweep range
      now + debrisDelay + debrisDuration,
    );

    // Connect audio graph
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.game!.masterGain);

    // Start and stop
    oscillator.start(now + debrisDelay);
    oscillator.stop(now + debrisDelay + debrisDuration);

    // Cleanup
    oscillator.addEventListener("ended", () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    });
  }
}
